import { useState } from 'react';
import { dossierApi } from '../../api/resources';
import { COTE_MAX, reduitLImage } from '../../lib/image';
import { TAILLE_MAX_PIECE, enMo, estEnregistre } from '../../lib/medias';
import { C } from '../../theme/palette';

/**
 * Fichier d'une pièce du dossier de l'employé : le scan ou la photo de la
 * pièce d'identité, du permis, du contrat signé, d'un diplôme…
 *
 * Le fichier part au stockage du serveur dès qu'il est choisi ; la fiche n'en
 * garde que l'existence et le nom. Il ne se consulte que par la direction.
 *
 * @param {object} props propriétés du composant
 * @param {number|string} props.employeId employé concerné
 * @param {object} props.piece pièce du dossier
 * @param {(piece: object) => void} props.onChange pièce à jour, après dépôt ou retrait
 * @returns {import('react').ReactElement} les commandes du fichier
 */
export function FichierDossier({ employeId, piece, onChange }) {
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');

  if (!estEnregistre(employeId) || !estEnregistre(piece.id)) {
    return (
      <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
        📎 Enregistre la fiche pour joindre le fichier (scan/photo).
      </div>
    );
  }

  const depose = async (evenement) => {
    const fichier = evenement.target.files && evenement.target.files[0];
    evenement.target.value = '';
    if (!fichier) return;
    setErreur('');
    setEnvoi(true);
    try {
      const reduit = await reduitLImage(fichier, COTE_MAX);
      if (reduit.size > TAILLE_MAX_PIECE) {
        setErreur(
          `Ce fichier pèse ${enMo(reduit.size)} Mo, au-delà des ${enMo(TAILLE_MAX_PIECE)} Mo admis.`,
        );
        return;
      }
      const dto = await dossierApi.joindre(employeId, piece.id, reduit, fichier.name);
      onChange({ ...piece, fichier: dto.fichierJoint === true, nomFichier: dto.nomFichier || '' });
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué.');
    } finally {
      setEnvoi(false);
    }
  };

  const retire = async () => {
    if (!window.confirm('Retirer ce fichier ? Il sera effacé du serveur.')) return;
    setErreur('');
    try {
      await dossierApi.retire(employeId, piece.id);
      onChange({ ...piece, fichier: false, nomFichier: '' });
    } catch (e) {
      setErreur(e?.message || 'Le retrait a échoué.');
    }
  };

  return (
    <div style={{ marginTop: 5 }}>
      {piece.fichier ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <a
            href={dossierApi.adresse(employeId, piece.id)}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 11, fontWeight: 700, color: C.navy }}
          >
            📎 {piece.nomFichier || 'Voir le fichier'}
          </a>
          <button
            type="button"
            onClick={retire}
            style={{
              background: 'none',
              border: 'none',
              color: C.red,
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ✕ Retirer le fichier
          </button>
        </div>
      ) : null}
      <label
        style={{
          display: 'inline-block',
          marginTop: 4,
          fontSize: 11,
          fontWeight: 700,
          color: C.teal,
          cursor: envoi ? 'wait' : 'pointer',
          opacity: envoi ? 0.6 : 1,
        }}
      >
        {envoi
          ? 'Envoi en cours…'
          : piece.fichier
            ? '📎 Remplacer le fichier'
            : '📎 Joindre le fichier (scan/photo)'}
        <input
          type="file"
          accept="image/*,application/pdf"
          aria-label="Fichier de la pièce"
          disabled={envoi}
          style={{ display: 'none' }}
          onChange={depose}
        />
      </label>
      {erreur && (
        <div role="alert" style={{ fontSize: 11, color: C.red, marginTop: 4 }}>
          {erreur}
        </div>
      )}
    </div>
  );
}
